import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import CampaignIcon from '@mui/icons-material/Campaign';
import ImageIcon from '@mui/icons-material/Image';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import BusinessUnitSelector from '../../components/common/BusinessUnitsSelector';
import { useGetAggregatedLeadsByBusinessUnitQuery } from '../../services/api/leads.api';
import { useLazyGetZellerAdsHierarchyQuery } from '../../services/api/zellerAds.api';
import type { MetaAd, MetaCampaign, MetaAdAccount, MetaAdSet } from '../../services/api/zellerAds.api';

interface CampaignWithLeads {
  campaign: MetaCampaign;
  totalLeads: number;
  adsWithLeads: Array<{
    ad: MetaAd;
    adSet: MetaAdSet;
    leadsCount: number;
  }>;
}

interface DashboardStats {
  totalCampaigns: number;
  totalAds: number;
  totalLeads: number;
  campaignsWithLeads: number;
}

const ZellerAdsLeads = () => {
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(window.location.search);
  const queryBusinessUnitId = queryParams.get('businessUnitId');
  const businessUnitId = queryBusinessUnitId ? parseInt(queryBusinessUnitId || '', 10) : null;

  const [getHierarchy, { data: hierarchyData, isLoading: hierarchyLoading, error: hierarchyError }] =
    useLazyGetZellerAdsHierarchyQuery();

  const {
    data: aggregatedLeadsData,
    isLoading: leadsLoading,
    error: leadsError,
  } = useGetAggregatedLeadsByBusinessUnitQuery({ businessUnitId: businessUnitId || 0 }, { skip: !businessUnitId });

  const updateBusinessUnitId = (value: number) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('businessUnitId', value.toString());
    navigate(`${window.location.pathname}?${searchParams.toString()}`, { replace: true });
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'success';
      case 'PAUSED':
        return 'warning';
      case 'ARCHIVED':
        return 'error';
      default:
        return 'default';
    }
  };

  const campaignLeadsData = useMemo((): CampaignWithLeads[] => {
    if (!hierarchyData || !aggregatedLeadsData) return [];

    const leadsMap = new Map(aggregatedLeadsData.aggregatedLeads.map((lead) => [lead.sourceId, lead.leadCount]));
    const campaignsWithLeads: CampaignWithLeads[] = [];

    if (hierarchyData.adAccounts && Array.isArray(hierarchyData.adAccounts)) {
      hierarchyData.adAccounts.forEach((adAccount: MetaAdAccount) => {
        if (adAccount && adAccount.campaigns && Array.isArray(adAccount.campaigns)) {
          adAccount.campaigns.forEach((campaign: MetaCampaign) => {
            if (campaign && campaign.adSets && Array.isArray(campaign.adSets)) {
              const adsWithLeads: Array<{
                ad: MetaAd;
                adSet: MetaAdSet;
                leadsCount: number;
              }> = [];

              let totalCampaignLeads = 0;

              campaign.adSets.forEach((adSet) => {
                if (adSet && adSet.ads && Array.isArray(adSet.ads)) {
                  adSet.ads.forEach((ad: MetaAd) => {
                    if (ad && ad.id) {
                      const leadCount = leadsMap.get(ad.id) || 0;

                      if (leadCount > 0) {
                        adsWithLeads.push({
                          ad,
                          adSet,
                          leadsCount: leadCount,
                        });
                        totalCampaignLeads += leadCount;
                      }
                    }
                  });
                }
              });

              if (totalCampaignLeads > 0) {
                campaignsWithLeads.push({
                  campaign,
                  totalLeads: totalCampaignLeads,
                  adsWithLeads,
                });
              }
            }
          });
        }
      });
    }

    return campaignsWithLeads.sort((a, b) => b.totalLeads - a.totalLeads);
  }, [hierarchyData, aggregatedLeadsData]);

  const dashboardStats = useMemo((): DashboardStats => {
    if (!hierarchyData || !aggregatedLeadsData) {
      return {
        totalCampaigns: 0,
        totalAds: 0,
        totalLeads: 0,
        campaignsWithLeads: 0,
      };
    }

    return {
      totalCampaigns: hierarchyData.stats?.campaigns || 0,
      totalAds: hierarchyData.stats?.ads || 0,
      totalLeads: aggregatedLeadsData.totalLeads,
      campaignsWithLeads: campaignLeadsData.length,
    };
  }, [hierarchyData, aggregatedLeadsData, campaignLeadsData]);

  useEffect(() => {
    if (businessUnitId) {
      // eslint-disable-next-line no-void
      void getHierarchy({ businessUnitId });
    }
  }, [businessUnitId, getHierarchy]);

  const isLoading = hierarchyLoading || leadsLoading;
  const hasError = hierarchyError || leadsError;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Dashboard Meta Ads - Leads
      </Typography>

      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Visualiza las campañas de Meta Ads con sus leads asociados
      </Typography>

      <Box sx={{ mb: 3 }}>
        <BusinessUnitSelector businessUnitId={businessUnitId} setBusinessUnitId={updateBusinessUnitId} small />
      </Box>

      {!businessUnitId && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Selecciona una unidad de negocio para ver el dashboard de Meta Ads con leads
        </Alert>
      )}

      {businessUnitId && isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {businessUnitId && hasError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error al cargar los datos. Por favor, inténtalo de nuevo.
        </Alert>
      )}

      {businessUnitId && !isLoading && !hasError && hierarchyData && aggregatedLeadsData && (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CampaignIcon color="primary" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" color="primary">
                        {dashboardStats.totalCampaigns}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Campañas Totales
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TrendingUpIcon color="success" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" color="success.main">
                        {dashboardStats.campaignsWithLeads}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Campañas con Leads
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <ImageIcon color="info" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" color="info.main">
                        {dashboardStats.totalAds}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Anuncios Totales
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LeaderboardIcon color="warning" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" color="warning.main">
                        {dashboardStats.totalLeads.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Leads Totales
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {campaignLeadsData.length === 0 ? (
            <Alert severity="info">No se encontraron campañas con leads asociados para esta unidad de negocio.</Alert>
          ) : (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Campañas con Leads ({campaignLeadsData.length})
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {campaignLeadsData.map((campaignData, index) => (
                  <Box key={campaignData.campaign.id} sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <CampaignIcon color="primary" />
                      <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        {campaignData.campaign.name}
                      </Typography>
                      <Chip
                        label={campaignData.campaign.status}
                        color={getStatusColor(campaignData.campaign.status)}
                        size="small"
                      />
                      <Chip
                        label={`${campaignData.totalLeads.toLocaleString()} leads`}
                        color="primary"
                        variant="outlined"
                      />
                    </Box>

                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <strong>Conjunto de Anuncios</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Anuncio</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Estado</strong>
                            </TableCell>
                            <TableCell align="center">
                              <strong>Leads</strong>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {campaignData.adsWithLeads.map((adData) => (
                            <TableRow key={adData.ad.id}>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {adData.adSet.name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <ImageIcon fontSize="small" color="action" />
                                  {adData.ad.name}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip label={adData.ad.status} color={getStatusColor(adData.ad.status)} size="small" />
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={adData.leadsCount.toLocaleString()} color="success" size="small" />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {index < campaignLeadsData.length - 1 && <Divider sx={{ mt: 3 }} />}
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Última actualización: {new Date(hierarchyData.retrievedAt).toLocaleDateString('es-CL')} • Datos agregados
            para optimizar rendimiento
          </Typography>
        </>
      )}
    </Box>
  );
};

export default ZellerAdsLeads;
